/**
 * Offline Queue Service
 *
 * Queues create/update/delete operations when offline and syncs when back online (FR-036).
 * Supports offline operation queuing and synchronization.
 */

import { offlineCacheService } from './offline-cache.service';
import { CACHE_KEYS, OFFLINE_QUEUE_CONFIG } from '../config/cache-config';
import { getApiUrl } from '../config/api';

export type QueuedOperationType = 'CREATE' | 'UPDATE' | 'DELETE';

export interface QueuedOperation {
  id: string;
  type: QueuedOperationType;
  entity: string;
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  payload?: unknown;
  timestamp: number;
  retries: number;
}

export interface OfflineQueueStatus {
  isOnline: boolean;
  pendingCount: number;
  isSyncing: boolean;
  lastSyncAt: number | null;
  lastError: string | null;
}

type StatusListener = (status: OfflineQueueStatus) => void;

/**
 * Manages offline operation queue and synchronization.
 */
export class OfflineQueueService {
  private listeners = new Set<StatusListener>();
  private isSyncing = false;
  private syncAbortController: AbortController | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.handleOnline());
      window.addEventListener('offline', () => this.notifyListeners());
    }
  }

  enqueue(operation: Omit<QueuedOperation, 'id' | 'timestamp' | 'retries'>): void {
    const queue = this.getQueue();
    if (queue.length >= OFFLINE_QUEUE_CONFIG.maxQueueSize) {
      throw new Error('Offline queue is full. Please try again when online.');
    }

    const queued: QueuedOperation = {
      ...operation,
      id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      timestamp: Date.now(),
      retries: 0,
    };

    queue.push(queued);
    this.saveQueue(queue);
    this.notifyListeners();
  }

  getQueue(): QueuedOperation[] {
    const raw = offlineCacheService.get<QueuedOperation[]>(CACHE_KEYS.OFFLINE_QUEUE);
    return Array.isArray(raw) ? raw : [];
  }

  private saveQueue(queue: QueuedOperation[]): void {
    offlineCacheService.set(CACHE_KEYS.OFFLINE_QUEUE, queue, 0);
  }

  async sync(): Promise<{ success: number; failed: number; errors: string[] }> {
    if (this.isSyncing) {
      return { success: 0, failed: 0, errors: ['Sync already in progress'] };
    }

    if (!navigator.onLine) {
      return { success: 0, failed: 0, errors: ['Device is offline'] };
    }

    this.isSyncing = true;
    this.syncAbortController = new AbortController();
    this.notifyListeners();

    const queue = this.getQueue();
    let success = 0;
    let failed = 0;
    const errors: string[] = [];

    for (const op of queue) {
      if (op.retries >= OFFLINE_QUEUE_CONFIG.maxRetries) {
        errors.push(`Operation ${op.id} exceeded max retries`);
        failed++;
        continue;
      }

      try {
        const response = await fetch(getApiUrl(op.endpoint), {
          method: op.method,
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('auth_token') && {
              Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            }),
          },
          body: op.payload ? JSON.stringify(op.payload) : undefined,
          signal: this.syncAbortController?.signal,
        });

        if (response.ok) {
          success++;
          const newQueue = this.getQueue().filter((q) => q.id !== op.id);
          this.saveQueue(newQueue);
        } else {
          op.retries++;
          const updated = this.getQueue().map((q) =>
            q.id === op.id ? op : q
          );
          this.saveQueue(updated);
          failed++;
          errors.push(`Operation ${op.id}: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        op.retries++;
        const updated = this.getQueue().map((q) =>
          q.id === op.id ? op : q
        );
        this.saveQueue(updated);
        failed++;
        const msg = error instanceof Error ? error.message : 'Unknown error';
        errors.push(`Operation ${op.id}: ${msg}`);
      }
    }

    if (success > 0) {
      offlineCacheService.set(CACHE_KEYS.LAST_SYNC, Date.now(), 0);
    }

    this.isSyncing = false;
    this.syncAbortController = null;
    this.notifyListeners();

    return { success, failed, errors };
  }

  private handleOnline(): void {
    this.sync().catch((err) => {
      console.warn('[OfflineQueue] Auto-sync failed:', err);
    });
  }

  getStatus(): OfflineQueueStatus {
    return {
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      pendingCount: this.getQueue().length,
      isSyncing: this.isSyncing,
      lastSyncAt: offlineCacheService.get<number>(CACHE_KEYS.LAST_SYNC),
      lastError: null,
    };
  }

  subscribe(listener: StatusListener): () => void {
    this.listeners.add(listener);
    listener(this.getStatus());
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    const status = this.getStatus();
    this.listeners.forEach((l) => l(status));
  }

  clearQueue(): void {
    this.saveQueue([]);
    this.notifyListeners();
  }
}

export const offlineQueueService = new OfflineQueueService();
